// Shared helpers for annotating alt / aria-label text values in the i18n debug overlay.
// Centralizing this logic reduces duplication between altAnnotator.js and textScanner.js
// while keeping behavior consistent.
import { SENT, SENTINEL_REGEX } from './constants'

function debugI18nOverlay(message, err) {
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug(`[i18n overlay] ${message}`, err)
  }
}

function parseSentinel(raw) {
  if (!raw) return { baseLabel: raw, sentinelKey: null }
  if (!raw.includes(SENT.ORANGE_PREFIX) && !raw.includes(SENT.MISS_PREFIX)) {
    return { baseLabel: raw, sentinelKey: null }
  }
  try {
    const m = SENTINEL_REGEX.exec(raw)
    if (m) return { baseLabel: m[3], sentinelKey: m[2] }
  } catch (err) {
    // Non‑critical: failed to parse sentinel wrapper; log in dev for diagnostics.
    debugI18nOverlay('parseSentinel failed', err)
  }
  return { baseLabel: raw, sentinelKey: null }
}

function matchTemplate(trimmed, patterns = []) {
  return patterns.find?.(({ regex }) => {
    try {
      return regex.test(trimmed)
    } catch (err) {
      debugI18nOverlay('regex.test failed in matchTemplate', err)
      return false
    }
  })
}

function resolveUniqueKey(trimmed, enValueToKeys, sentinelKey, templateMatch) {
  if (sentinelKey || templateMatch || !enValueToKeys?.get) return null
  const set = enValueToKeys.get(trimmed)
  if (set && set.size === 1) return Array.from(set)[0]
  return null
}

// Core algorithm used by both annotators.
// Returns { label, isHardCoded, isOkBoth }
export function computeAltAriaAnnotation(rawAlt, meta) {
  if (!rawAlt) return null
  const { esKeys, enValueToKeys, enValuePatterns = [] } = meta || {}
  const hasEsInfo = !!(esKeys && typeof esKeys.has === 'function')

  const { baseLabel, sentinelKey } = parseSentinel(rawAlt)
  let label = baseLabel
  const trimmed = (label || '').trim()
  const templateMatch = matchTemplate(trimmed, enValuePatterns)
  const uniqueMapKey = resolveUniqueKey(trimmed, enValueToKeys, sentinelKey, templateMatch)
  const hasExplicitEvidence = !!sentinelKey || !!templateMatch || !!uniqueMapKey
  const isHardCoded = !hasExplicitEvidence && /[a-zA-Z]/.test(trimmed) && trimmed.length > 1

  // Determine translation presence in both locales
  let isOkBoth = false
  if (sentinelKey) isOkBoth = !!esKeys?.has?.(sentinelKey)
  else if (templateMatch) isOkBoth = !!esKeys?.has?.(templateMatch.key)
  else if (uniqueMapKey) isOkBoth = !!esKeys?.has?.(uniqueMapKey)

  // If ES key missing but we know the key, surface key instead of EN value
  if (hasEsInfo && !isOkBoth) {
    if (sentinelKey) label = sentinelKey
    else if (templateMatch) label = templateMatch.key
    else if (uniqueMapKey) label = uniqueMapKey
  }

  if (isHardCoded) label = `${label} - HARD-CODED`
  if (!label.startsWith('(ALT)')) label = `(ALT) ${label}`

  return { label, isHardCoded, isOkBoth }
}
